import type {
    IBuyerOrderFilters,
    IFindManyReq as IBuyerOrderQueryInput,
} from "@agrigate/commodity-transaction-api/lib/services/BuyerOrdersService";
import type { ICollectionTargetFilters } from "@agrigate/commodity-transaction-api/lib/services/CollectionTargetsService";
import type { IProduceBatchFilter } from "@agrigate/commodity-transaction-api/lib/services/ProduceBatchesService";
import type { IAlertCode } from "@agrigate/domain";
import { AlertCode } from "@agrigate/domain";
import type { IAlert } from "@agrigate/notification-types";
import type { IUserOrderPerformanceMetricsResult } from "@agrigate/reporting-api/lib/sql/reporting/user-order-performance-metrics.queries";
import {
    predefinedRanges,
    rangeLabelToDateRange,
} from "@agrigate/shared/lib/consts/predefined-date-ranges";
import useAsyncTask from "@agrigate/shared/lib/hooks/use-async-task";
import {
    customTimeAgo,
    formatMoney,
} from "@agrigate/shared/lib/utils/formatters";
import BlockDropDown from "@agrigate/shared-web-ui/lib/BlockDropDown";
import ClusterDropDown from "@agrigate/shared-web-ui/lib/ClusterDropDown";
import ContentColumn from "@agrigate/shared-web-ui/lib/ContentColumn";
import DistrictDropDown from "@agrigate/shared-web-ui/lib/DistrictDropDown";
import FormItem from "@agrigate/shared-web-ui/lib/FormItem";
import { useIsDesktop } from "@agrigate/shared-web-ui/lib/hooks/use-is-desktop";
import MenuButtonCombo from "@agrigate/shared-web-ui/lib/MenuButtonCombo";
import PopoverCombo from "@agrigate/shared-web-ui/lib/PopoverCombo";
import RegionDropDown from "@agrigate/shared-web-ui/lib/RegionDropdown";
import SwitchButtonCombo from "@agrigate/shared-web-ui/lib/SwitchButtonCombo";
import { thruUnlessN } from "@agrigate/ts-utils/lib/nullability";
import type { IUserFilters } from "@agrigate/user-management-api/lib/services/UsersService";
import type { BoxProps } from "@chakra-ui/react";
import {
    Box,
    Button,
    Flex,
    Heading,
    Image,
    Input,
    Separator,
    Spacer,
    Span,
    Text,
} from "@chakra-ui/react";
import { formatDistanceToNowStrict } from "date-fns";
import { useAtomValue } from "jotai";
import type React from "react";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { IoAddOutline, IoFilter } from "react-icons/io5";
import { MdCallMade } from "react-icons/md";
import { useNavigate } from "react-router";
import BackImg from "../assets/dashboard_card_back.svg";
import BuyerDashIcon from "../assets/icons/buyer_dash_icon.svg?react";
import CPDashIcon from "../assets/icons/cp_dash_icon.svg?react";
import CPRegisteredIcon from "../assets/icons/cp_registered.svg?react";
import EmployeeDashIcon from "../assets/icons/employee_dash_icon.svg?react";
import FarmerDashIcon from "../assets/icons/farmer_dash_icon.svg?react";
import FarmerLotIcon from "../assets/icons/farmer_lot_icon.svg?react";
import ProcFulFillIcon from "../assets/icons/procurement_fulfilled.svg?react";
import ShipmentDelivered from "../assets/icons/shipment_delivered.svg?react";
import InsightApproaching from "../assets/icons/upcoming_market_insight.svg?react";
import InventoryOverview from "../components/InventoryComp";
import { useGeoScope } from "../hooks/use-geo-scope";
import { usePageTitle } from "../stores/navItems";
import { sessionAtom } from "../stores/session";
import { isAppScopeContentManager } from "../utils/app-scope";
import rpc, { reportingRPC } from "../utils/rpc";

export const title = "Dashboard";

interface IPerformanceMetricFilter {
    regionId?: string | null;
    districtId?: string | null;
    blockId?: string | null;
    clusterId?: string | null;
    pinCode?: string | null;
}

export default function DashboardPage() {
    usePageTitle(title);

    const navigate = useNavigate();

    const session = useAtomValue(sessionAtom);

    const menuOptions = [
        {
            key: "Order",
            label: "Order",
            subMenu: [
                {
                    key: "Procurement Order",
                    label: "Procurement Order",
                    onClick: () => navigate("/procurement/new"),
                },
                {
                    key: "Buyer Order",
                    label: "Buyer Order",
                    onClick: () => navigate("/buyer-orders/new"),
                },
                {
                    key: "Shipment",
                    label: "Shipment",
                    onClick: () => navigate("/shipments/new"),
                },
            ],
        },
        {
            key: "Users",
            label: "Users",
            onClick: () => {
                console.log("Users menu clicked");
            },
            subMenu: [
                {
                    key: "Farmer",
                    label: "Farmer",
                    onClick: () => {
                        console.log("Farmer menu clicked");
                    },
                },
                {
                    key: "Buyer",
                    label: "Buyer",
                    onClick: () => {
                        console.log("Buyer menu clicked");
                    },
                },
                {
                    key: "Channel Partner",
                    label: "Channel Partner",
                    onClick: () => {
                        console.log("CP menu clicked");
                    },
                },
            ],
        },
        {
            key: "Feed",
            label: "Feed",
            onClick: () =>
                navigate("/app-content/farmer-feed?type=custom-feed"),
        },
        {
            key: "Market Insight",
            label: "Market Insight",
            onClick: () => {
                console.log("Market ins menu clicked");
            },
        },
        {
            key: "Notification",
            label: "Notification",
            onClick: () => {
                console.log("Notifi menu clicked");
            },
        },
    ];

    const isDesktop = useIsDesktop();

    if (isAppScopeContentManager()) {
        return (
            <ContentColumn>
                <Flex
                    flexDirection="row"
                    my={6}
                    px={4}
                    py={4}
                    borderRadius={10}
                    bgColor="#FBF0D4"
                    alignItems="center"
                >
                    <Text fontSize={"xx-large"}>👋</Text>
                    <Flex flex="1" flexDir="column" mx={4}>
                        <Heading
                            fontSize={16}
                        >{`Welcome back, ${session?.fullName}`}</Heading>
                        <Text fontSize={14} mt={1} color="GrayText">
                            Select an option from sidebar
                        </Text>
                    </Flex>
                </Flex>
            </ContentColumn>
        );
    }

    return (
        <ContentColumn>
            <Flex
                flexDirection="row"
                my={6}
                px={4}
                py={4}
                borderRadius={10}
                bgColor="#FBF0D4"
                alignItems="center"
            >
                <Text fontSize={"xx-large"}>👋</Text>
                <Flex flex="1" flexDir="column" mx={4}>
                    <Heading
                        fontSize={16}
                    >{`Welcome back, ${session?.fullName}`}</Heading>
                    <Text fontSize={14} mt={1} color="GrayText">
                        Here's a quick glance at your orders and tasks for the
                        day.
                    </Text>
                </Flex>
                <MenuButtonCombo
                    size="sm"
                    title={"Create New"}
                    colorPalette="yellow"
                    variant="outline"
                    icon={<IoAddOutline fontSize={10} />}
                    styles={{
                        button: {
                            background: "rgba(0,0,0,0.05)",
                        },
                        menu: {
                            padding: "0px",
                            overflow: "hidden",
                        },
                    }}
                    options={menuOptions}
                />
            </Flex>
            <Flex mt={10} gap={2} flexDir={["column", "row"]}>
                <PendingSection />
                <CriticalAlertSection />
            </Flex>
            <PerformanceMetrics />
            <InventoryOverview />
            <UserListSection />
        </ContentColumn>
    );
}

const PendingSection = () => {
    const orderData = useAsyncTask(
        async () => await reportingRPC.dashMetrics.getOrderDataOverview(),
        {
            deps: [],
        },
    );

    const cards = [
        {
            key: "incomingBO",
            title: "Incoming Buyers Orders",
            count: orderData.data?.at(0)?.incomingBuyerOrderCount ?? 0,
            newCount: 4,
            navLink: `/buyer-orders/list?f=${encodeURIComponent(
                JSON.stringify({
                    status: {
                        eq: "SCANNING",
                    },
                } satisfies IBuyerOrderFilters),
            )}`,
        },
        {
            key: "incomingSO",
            title: "Incoming Farmer Lots",
            count: orderData.data?.at(0)?.incomingProduceBatchCount ?? 0,
            newCount: 4,
            navLink: `/seller-orders/list?f=${encodeURIComponent(
                JSON.stringify({
                    status: {
                        eq: "SCANNING",
                    },
                } satisfies IProduceBatchFilter),
            )}`,
        },
    ];
    return (
        <Flex direction="column">
            <Heading fontSize={16}>Pending Items</Heading>
            <Box h={430} width={400} mt={3}>
                <Box
                    position="relative"
                    overflow="hidden"
                    height={296}
                    width={400}
                >
                    {/* Background Image */}
                    <Image
                        src={BackImg}
                        alt="Background"
                        width="100%"
                        objectFit="cover"
                    />
                    {/* Overlay Content */}
                    <Flex
                        position="absolute"
                        top="0"
                        left="0"
                        width="100%"
                        height="100%"
                        direction="column"
                        justify="center"
                        align="center"
                        p={4}
                    >
                        {thruUnlessN(
                            orderData.data?.at(0)?.totalOutstandingAmount,
                            amt => (
                                <>
                                    <Text color="gray">
                                        Total Outstanding Amount
                                    </Text>
                                    <Heading my={2} size="lg">
                                        {`${formatMoney(amt)}`}
                                    </Heading>
                                </>
                            ),
                        )}
                        {thruUnlessN(
                            orderData.data?.at(0)?.uniqueBuyerOrderCount,
                            count => (
                                <>
                                    <Button
                                        size="sm"
                                        colorPalette="gray"
                                        background="white"
                                        onClick={() => {
                                            window.open(
                                                `/buyer-orders/list?f=${encodeURIComponent(
                                                    JSON.stringify({
                                                        isDue: true,
                                                    } satisfies IBuyerOrderFilters),
                                                )}`,
                                            );
                                        }}
                                    >
                                        {`${count} Orders`}
                                    </Button>
                                </>
                            ),
                        )}
                    </Flex>
                </Box>
                <Box height={2} />
                <Flex columnGap={2}>
                    {cards.map(item => (
                        <Box
                            key={item.key}
                            flex={1}
                            px={4}
                            py={4}
                            height={126}
                            cursor="pointer"
                            borderRadius={10}
                            bgColor="white"
                            onClick={() => {
                                window.open(item.navLink);
                            }}
                        >
                            <Flex flexDir="column" height="100%">
                                <Text flex={1} fontSize={15}>
                                    {`${item.title}`}
                                </Text>
                                <Flex alignItems="center">
                                    <Heading py={1} fontSize={24}>
                                        {item.count}
                                    </Heading>
                                    <Spacer />
                                    <MdCallMade color={"gray"} />
                                </Flex>
                            </Flex>
                        </Box>
                    ))}
                </Flex>
            </Box>
        </Flex>
    );
};

const CriticalAlertSection = () => {
    const navigate = useNavigate();

    const getAlertIcons = (code: IAlertCode) => {
        switch (code) {
            case AlertCode.enum.BUYER_ORDER_SUBMITTED:
                return <BuyerDashIcon />;
            case AlertCode.enum.PROCUREMENT_FULFILLED:
                return <ProcFulFillIcon />;
            case AlertCode.enum.MARKET_INSIGHT_DEADLINE_APPROACHING:
                return <InsightApproaching />;
            case AlertCode.enum.CHANNEL_PARTNER_REGISTERED:
                return <CPRegisteredIcon />;
            case AlertCode.enum.PRODUCE_BATCH_SUBMITTED:
                return <FarmerLotIcon />;
            case AlertCode.enum.SHIPMENT_DELIVERED:
                return <ShipmentDelivered />;
            default:
                return <MdCallMade size={28} />;
        }
    };

    const dataT = useAsyncTask(async () => await rpc.alerts.findMany({}), {
        deps: [],
    });

    return (
        <Flex flex={1} direction="column">
            <Heading fontSize={16}>Critical Alerts</Heading>
            <Box
                my={3}
                p={1}
                bgColor="white"
                borderRadius={10}
                height={430}
                overflowY="auto"
                className="scroller"
            >
                {dataT.data?.alerts && dataT.data.alerts.length > 0 ? (
                    dataT.data.alerts.map(item => (
                        <Flex
                            key={item.id}
                            py={4}
                            px={4}
                            onClick={() => {
                                const url = generateRedirURLforAlert(item);
                                if (url) navigate(url);
                            }}
                            cursor="pointer"
                            _hover={{
                                bgColor: "#F2FBF5",
                                borderRadius: "10px",
                            }}
                        >
                            {getAlertIcons(item.code)}
                            <Flex flex={1} flexDir="column">
                                <Flex>
                                    <Heading flex={1} fontSize={12} px={4}>
                                        {item.title}
                                    </Heading>
                                    <Text color="gray" fontSize={12}>
                                        {customTimeAgo(item.generatedAt)}
                                    </Text>
                                </Flex>
                                <Text fontSize={12} px={4}>
                                    {generateAlertMsgHTML(item)}
                                </Text>
                            </Flex>
                        </Flex>
                    ))
                ) : (
                    <Text
                        py={4}
                        textAlign="center"
                        fontSize={14}
                        fontWeight={600}
                    >
                        No alerts available!
                    </Text>
                )}
            </Box>
        </Flex>
    );
};

const PerformanceMetrics = () => {
    const timeArr = predefinedRanges.slice(0, 6);

    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filters, setFilters] = useState<
        IPerformanceMetricFilter | undefined
    >();
    const [timeFrame, setTimeFrame] = useState<string>(timeArr[2].label);

    const session = useAtomValue(sessionAtom);
    const { pinnedIds } = useGeoScope();

    const perfData = useAsyncTask(
        async () => {
            const selTimeFrame = timeArr.find(
                item => item.label === timeFrame,
            )?.value;

            return await reportingRPC.dashMetrics.userOrderPerformanceMetrics({
                startDate: selTimeFrame && +selTimeFrame[0],
                endDate: selTimeFrame && +selTimeFrame[1],
                clusterId: filters?.clusterId,
                regionId: filters?.regionId,
                pinCode: filters?.pinCode,
            });
        },
        {
            deps: [filters, timeFrame],
        },
    );

    const userData = perfData.data?.filter(
        item => item.category === "user_stats",
    );
    const satisfiedOrder = perfData.data?.filter(
        item => item.category === "satisfied_order",
    );

    const defaultFilters = useMemo(
        () => ({
            regionId: pinnedIds.regionId,
            clusterId: pinnedIds.clusterId,
        }),
        [pinnedIds],
    );

    useEffect(() => {
        if (filters) return;
        setFilters(defaultFilters);
    }, [session]);

    const isDesktop = useIsDesktop();
    const navigate = useNavigate();

    return (
        <Flex mt={10} flexDir="column">
            <Flex alignItems="center" flexWrap={"wrap"}>
                <Heading fontSize={16}>Performance Metrics</Heading>
                <Spacer />
                <PopoverCombo
                    closeOnBlur={false}
                    isOpen={filtersOpen}
                    onOpen={() => setFiltersOpen(true)}
                    onClose={() => setFiltersOpen(false)}
                    renderTrigger={() => (
                        <Button
                            size="sm"
                            variant="outline"
                            bgColor="white"
                            color="gray"
                            borderColor="gray.200"
                        >
                            {<IoFilter />}
                            Filters
                        </Button>
                    )}
                    header="Filters"
                >
                    <PerformanceFilterForm
                        {...{
                            filters,
                            setFilters,
                            onExit: () => setFiltersOpen(false),
                        }}
                    />
                </PopoverCombo>
                <Box width={4} />
                <SwitchButtonCombo
                    options={timeArr.map(value => ({
                        value: value.label,
                        label: value.label,
                    }))}
                    selected={timeFrame}
                    onChange={t => setTimeFrame(t)}
                    isAttached={isDesktop}
                    styles={{
                        buttons: {
                            deselected: {
                                backgroundColor: "white",
                            },
                        },
                        group: {
                            flexWrap: "wrap",
                            gap: isDesktop ? "0px" : "5px",
                        },
                    }}
                />
            </Flex>
            <Flex gap={2} flexDir={["column", "row"]}>
                <UserPerformance
                    {...{
                        userData,
                        timeFrame,
                        regionId: filters?.regionId,
                        clusterId: filters?.clusterId,
                        pinCode: filters?.pinCode,
                    }}
                />
                <Flex flexDir="column" flex={1} mt={2}>
                    <Flex columnGap={2}>
                        <StatBox
                            title="Buyer Order Satisfied"
                            onClick={() =>
                                navigate(
                                    `/buyer-orders/list?f=${encodeURIComponent(
                                        JSON.stringify({
                                            status: {
                                                eq: "DELIVERED",
                                            },
                                            pinCode: {
                                                eq: filters?.pinCode,
                                            },
                                            regionId: {
                                                eq: filters?.regionId,
                                            },
                                            clusterId: {
                                                eq: filters?.clusterId,
                                            },
                                            districtId: {
                                                eq: filters?.districtId,
                                            },
                                            settledAt: {
                                                inRange:
                                                    rangeLabelToDateRange(
                                                        timeFrame,
                                                    ),
                                            },
                                        } satisfies IBuyerOrderQueryInput["filters"]),
                                    )}`,
                                )
                            }
                            count={
                                satisfiedOrder?.find(
                                    item =>
                                        item.metric ===
                                        "buyer_satisfied_orders",
                                )?.count ?? 0
                            }
                            icon
                        />
                        <StatBox
                            title="Proc. Order Satisfied"
                            onClick={() =>
                                navigate(
                                    `/procurement/list?f=${encodeURIComponent(
                                        JSON.stringify({
                                            status: "COMPLETED",
                                            pinCode: {
                                                eq: filters?.pinCode,
                                            },
                                            regionId: {
                                                eq: filters?.regionId,
                                            },
                                            clusterId: {
                                                eq: filters?.clusterId,
                                            },
                                            districtId: {
                                                eq: filters?.districtId,
                                            },
                                            concludedAt:
                                                rangeLabelToDateRange(
                                                    timeFrame,
                                                ),
                                        } satisfies ICollectionTargetFilters),
                                    )}`,
                                )
                            }
                            count={
                                satisfiedOrder?.find(
                                    item =>
                                        item.metric === "proc_satisfied_orders",
                                )?.count ?? 0
                            }
                            icon
                        />
                    </Flex>
                    <StatBox
                        title="Response rate for procurement alerts"
                        icon
                        mt={2}
                    />
                </Flex>
            </Flex>
        </Flex>
    );
};

const StatBox: React.FC<
    {
        title: string;
        count?: number;
        icon?: boolean;
    } & BoxProps
> = props => {
    return (
        <Box
            flex={1}
            px={6}
            py={6}
            bgColor="white"
            borderRadius={10}
            {...props}
        >
            <Text fontSize={14}>{props.title}</Text>
            <Box h={2} />
            {props.icon ? (
                <Flex alignItems="center">
                    <Heading fontSize={20}>{props.count ?? "N/A"}</Heading>
                    <Spacer />
                    <MdCallMade color={"gray"} />
                </Flex>
            ) : (
                <Heading fontSize={20}>{props.count ?? "N/A"}</Heading>
            )}
        </Box>
    );
};

const UserPerformance = (p: {
    userData?: IUserOrderPerformanceMetricsResult[];
    timeFrame?: string;
    regionId?: string | null;
    clusterId?: string | null;
    pinCode?: string | null;
}) => {
    const navigate = useNavigate();

    const dateRange = useMemo(
        () => rangeLabelToDateRange(p.timeFrame),
        [p.timeFrame],
    );

    const generateNavLink = useCallback(
        (role?: string) =>
            `/users?f=${encodeURIComponent(
                JSON.stringify({
                    regionId: {
                        eq: p.regionId,
                    },
                    clusterId: {
                        eq: p.clusterId,
                    },
                    pinCode: {
                        eq: p.pinCode,
                    },
                    registeredAt: { inRange: dateRange },
                    role: { in: role ? [role] : null },
                } satisfies IUserFilters),
            )}`,
        [p],
    );

    const usersStatData = useMemo(() => {
        const metrics = [
            {
                metric: "farmer",
                title: "New Farmers",
                role: "OUTPUT_SELLER",
            },
            {
                metric: "channel-partner",
                title: "Channel Partners",
                role: "CHANNEL_PARTNER",
            },
            {
                metric: "buyer",
                title: "New Buyers",
                role: "OUTPUT_BUYER",
            },
        ];

        return metrics.map(({ metric, title, role }) => ({
            title,
            count: p.userData?.find(item => item.metric === metric)?.count ?? 0,
            navLink: generateNavLink(role),
        }));
    }, [p.userData, generateNavLink]);

    const totalCount = usersStatData.reduce((acc, item) => acc + item.count, 0);

    return (
        <Box flex={1} mt={2} borderRadius={10} bgColor="white">
            <StatBox
                title="Total No. of Users"
                count={totalCount}
                cursor="pointer"
                onClick={() => navigate(generateNavLink())}
                mb={1}
            />
            <Separator borderColor="gray.100" />
            <Flex mt={1}>
                {usersStatData.map((item, index) => (
                    <Flex key={item.title} flex={1}>
                        <StatBox
                            title={item.title}
                            count={item.count}
                            icon
                            cursor="pointer"
                            onClick={() => {
                                if (item.navLink) navigate(item.navLink);
                            }}
                        />
                        {index !== usersStatData.length - 1 && (
                            <Separator
                                orientation="vertical"
                                borderColor="gray.100"
                            />
                        )}
                    </Flex>
                ))}
            </Flex>
        </Box>
    );
};

const UserListSection = () => {
    const navigate = useNavigate();
    const countsT = useAsyncTask(
        async () => {
            const { counts } = await rpc.users.getCounts({});
            return {
                counts: counts.map(c => ({
                    count: c.count,
                    status: c.role,
                })),
            };
        },
        {
            deps: [],
        },
    );

    const genUserNavLink = useCallback(
        (p: { role?: string; notIn?: string; isEmployee?: boolean }) =>
            `/users?f=${encodeURIComponent(
                JSON.stringify({
                    role: {
                        in: p.role ? [p.role] : null,
                        notIn: p.notIn ? [p.notIn] : null,
                    },
                    isEmployee: p.isEmployee,
                } satisfies IUserFilters),
            )}`,
        [],
    );

    const userList = countsT.data?.counts
        .filter(item =>
            [
                "OUTPUT_SELLER",
                "EMPLOYEE",
                "OUTPUT_BUYER",
                "CHANNEL_PARTNER",
            ].includes(item.status),
        )
        .map(item => {
            switch (item.status) {
                case "OUTPUT_SELLER":
                    return {
                        label: "Farmers",
                        count: item.count,
                        icon: <FarmerDashIcon />,
                        navLink: genUserNavLink({
                            role: "OUTPUT_SELLER",
                            notIn: "ADMIN",
                        }),
                    };
                case "EMPLOYEE":
                    return {
                        label: "Employees",
                        count: item.count,
                        icon: <EmployeeDashIcon />,
                        navLink: genUserNavLink({ isEmployee: true }),
                    };
                case "OUTPUT_BUYER":
                    return {
                        label: "Buyers",
                        count: item.count,
                        icon: <BuyerDashIcon />,
                        navLink: genUserNavLink({
                            role: "OUTPUT_BUYER",
                            notIn: "ADMIN",
                        }),
                    };
                case "CHANNEL_PARTNER":
                    return {
                        label: "Channel Partner",
                        count: item.count,
                        icon: <CPDashIcon />,
                        navLink: genUserNavLink({
                            role: "CHANNEL_PARTNER",
                            notIn: "ADMIN",
                        }),
                    };

                default:
                    return {
                        label: "Farmers",
                        count: item.count,
                        icon: <CPDashIcon />,
                        navLink: "",
                    };
            }
        });

    return (
        <Flex mt={10} flexDir="column">
            <Heading fontSize={16}>Users</Heading>
            <Box bgColor="white" borderRadius={10} my={3}>
                {userList?.map((item, index) => (
                    <Flex
                        key={item.label}
                        cursor="pointer"
                        flexDir="column"
                        onClick={() => navigate(item.navLink)}
                    >
                        <Flex px={4} py={4} alignItems="center">
                            <Box pr={4}>{item.icon}</Box>
                            <Text flex={1} fontSize={14}>
                                {item.label}
                            </Text>
                            <Heading fontSize={16}>{item.count}</Heading>
                        </Flex>
                        {index !== userList.length - 1 && (
                            <Separator borderColor="gray.100" />
                        )}
                    </Flex>
                ))}
            </Box>
        </Flex>
    );
};

const generateAlertMsgHTML = (alert: IAlert): React.ReactNode => {
    if (
        !alert.message ||
        !alert.props?.highlights ||
        alert.props?.highlights?.length === 0
    ) {
        return alert.message;
    }

    if (
        ["MARKET_INSIGHT_DEADLINE_APPROACHING", "SHIPMENT_DELIVERED"].includes(
            alert.code,
        ) ||
        alert.message
    ) {
        let time = formatDistanceToNowStrict(alert.generatedAt);
        if (alert.code === "MARKET_INSIGHT_DEADLINE_APPROACHING") {
            time = `${time} away`;
        } else if (alert.code === "SHIPMENT_DELIVERED") {
            time = `${time}`;
        }
        alert.message = alert.message.replace("<N>", time);

        const prettifiedItem = alert.props.highlights.find(item =>
            item.text.includes("<N>"),
        );
        if (prettifiedItem) {
            prettifiedItem.text = prettifiedItem.text.replace("<N>", time);
        }
    }

    const result: React.ReactNode[] = [];
    let lastIndex = 0;

    alert.props.highlights.forEach(item => {
        const { text, color } = item;

        const startIndex = alert.message.indexOf(text, lastIndex);

        if (startIndex === -1) {
            return;
        }

        const endIndex = startIndex + text.length;

        if (startIndex > lastIndex) {
            result.push(alert.message.slice(lastIndex, startIndex));
        }

        const styledElement = (
            <span style={{ color }} key={startIndex}>
                {text}
            </span>
        );

        result.push(styledElement);

        lastIndex = endIndex;
    });

    if (lastIndex < alert.message.length) {
        result.push(alert.message.slice(lastIndex));
    }

    return result;
};

function PerformanceFilterForm(p: {
    filters?: IPerformanceMetricFilter;
    onExit?: () => void;
    setFilters: Dispatch<SetStateAction<IPerformanceMetricFilter | undefined>>;
}) {
    const formRef = useRef(null);

    const f = useForm<{
        regionId: string;
        districtId: string;
        blockId: string;
        clusterId: string;
        pinCode: string;
    }>({
        defaultValues: {
            regionId: p.filters?.regionId ?? "",
            districtId: p.filters?.districtId ?? "",
            blockId: p.filters?.blockId ?? "",
            clusterId: p.filters?.clusterId ?? "",
            pinCode: p.filters?.pinCode ?? "",
        },
    });

    const regionId = f.watch("regionId");
    const districtId = f.watch("districtId");
    const clusterId = f.watch("clusterId");
    const blockId = f.watch("blockId");

    return (
        <form
            ref={formRef}
            onSubmit={f.handleSubmit(data => {
                p.setFilters(prev => ({
                    ...prev,
                    regionId: data.regionId || undefined,
                    districtId: data.districtId || undefined,
                    clusterId: data.clusterId || undefined,
                    pinCode: data.pinCode || undefined,
                }));
                p.onExit?.();
            })}
        >
            <FormItem label="Region">
                <RegionDropDown
                    width={"100%"}
                    portalled={false}
                    onRegionChange={region => {
                        if (region) {
                            f.setValue("regionId", region);
                            f.setValue("clusterId", "");
                        } else {
                            f.setValue("regionId", "");
                            f.setValue("clusterId", "");
                        }
                    }}
                    regionId={regionId ?? ""}
                />
            </FormItem>
            <FormItem label="District">
                <DistrictDropDown
                    regionId={regionId}
                    districtId={districtId ?? ""}
                    width={"100%"}
                    portalled={false}
                    disabled={!regionId}
                    onChange={districtId => {
                        if (districtId) {
                            f.setValue("districtId", districtId);
                        } else {
                            f.setValue("districtId", "");
                        }
                    }}
                />
            </FormItem>
            <FormItem label="Block">
                <BlockDropDown
                    districtId={districtId}
                    blockId={blockId}
                    onChange={blockId => {
                        if (blockId) {
                            f.setValue("blockId", blockId);
                        } else {
                            f.setValue("blockId", "");
                        }
                    }}
                    width="100%"
                />
            </FormItem>
            <FormItem label="Cluster">
                <ClusterDropDown
                    regionId={regionId}
                    districtId={districtId}
                    clusterId={clusterId}
                    blockId={blockId}
                    width={"100%"}
                    portalled={false}
                    disabled={!regionId}
                    onChange={cluster => {
                        if (cluster) {
                            f.setValue("clusterId", cluster);
                        } else {
                            f.setValue("clusterId", "");
                        }
                    }}
                />
            </FormItem>
            <FormItem label="Pincode">
                <Input
                    {...f.register("pinCode", {
                        validate: val => {
                            if (val && val.length > 0 && val.length < 6) {
                                return "Pincode must be 6 digits";
                            }
                            return true;
                        },
                    })}
                    inputMode="numeric"
                    maxLength={6}
                />
                {f.formState.errors.pinCode && (
                    <Span color="red" fontSize="xs">
                        {f.formState.errors.pinCode.message}
                    </Span>
                )}
            </FormItem>
            <Flex direction="row" alignItems="center" gap={4} mt={4}>
                <Button type="submit">Submit</Button>
                <Button
                    variant="ghost"
                    onClick={() => {
                        p.setFilters(prev => ({
                            ...prev,
                            regionId: undefined,
                            districtId: undefined,
                            clusterId: undefined,
                            pinCode: undefined,
                        }));
                        f.reset();
                        f.setValue("regionId", "");
                        f.setValue("clusterId", "");
                        p.onExit?.();
                    }}
                >
                    Clear
                </Button>
            </Flex>
        </form>
    );
}

const generateRedirURLforAlert = (alert: IAlert) => {
    switch (alert.code) {
        case "BUYER_ORDER_SUBMITTED":
            return `/${alert.associatedEntity}/details?id=${alert.associatedId}`;
        case "CHANNEL_PARTNER_REGISTERED":
            return `/${alert.associatedEntity}/details?userId=${alert.associatedId}`;
        case "MARKET_INSIGHT_DEADLINE_APPROACHING":
            return `/${alert.associatedEntity}/list`;
        case "PRODUCE_BATCH_SUBMITTED":
            return `/${alert.associatedEntity}/details?pbId=${alert.associatedId}`;
        case "PROCUREMENT_FULFILLED":
            return `/${alert.associatedEntity}/details?id=${alert.associatedId}`;
        case "SHIPMENT_DELIVERED":
            return `/${alert.associatedEntity}/details?id=${alert.associatedId}`;
        case "XOXODAY_WALLET_LOW_BALANCE":
            return `/${alert.associatedEntity}/redemptions`;
        default:
            return "";
    }
};
